// S12 — «¿Qué me toca a mí, en qué orden?» (docs/oficina-v2/S12-ESTADOS-BRIEF.md). El servidor decide la
// situación de cada contratado (estado-visual.ts `situacion`); aquí, PURO, cómo se pinta: a qué grupo va, con
// qué icono y color (iguales en la lista, la cabecera del chat y la franja), qué frase lleva la fila y el
// resumen de una línea de la cabecera del panel. Nada de React salvo el tipo de los iconos.
import { Activity, ClipboardCheck, Handshake, KeyRound, MessageCircle, Pause, Pin, TriangleAlert, type LucideIcon } from 'lucide-react';
import { t } from '../../../i18n';
import type { ContratadoDTO, Situacion } from '../compartido/vocabulario';
import { ultimoMovimiento } from './estado';

/** Los grupos de la lista, de arriba abajo (brief: 1 → 2 → 3 → 4 → 5+6 «En marcha» → 7). */
// «problema_pau»: un problema técnico en el que Pau SÍ tiene que actuar (le pide algo) va justo detrás de 2 y
// cuenta en «lo que te toca»; el que arregla la oficina sola se queda abajo («problema»), en gris.
// 30-09 — «fijados» (los favoritos de Pau), cada uno con la insignia de su situación; desde el 06-10, por dentro, en
// subgrupos por estado con el mismo orden que el resto de la lista. 09-10 (Pau aprobó «los fijados no tapan un
// permiso», por su regla del 06-10 «lo que necesite mi atención lo más arriba posible»): un fijado al que le toca algo
// a Pau va a su grupo de urgencia (con una chincheta en la fila) y «Fijados» —solo los tranquilos— va justo detrás
// de lo urgente, antes de lo que pasa sin Pau.
// 29-10 (Pau: «cuando un agente se esté yendo, que se ponga en un sitio separado… que no moleste a la vista ni haga
// ruido, que estén ahí de fondo para tenerlos controlados»): «se_van», al final, plegado y sin avisos.
// 03-11 (rebrand; Pau: «si paso un chat a contrato con Santi que se me agrupe en otro lugar»): «con_santi», justo detrás de
// lo urgente y los fijados: el agente cuyo contrato tiene la pelota en el lado de Santi y no te necesita. Plegado y sin
// ruido (Pau, 01-11: lo de Santi «le mete ruido al trabajo con mis agentes»).
export const GRUPOS = ['autorizacion', 'bloqueado', 'problema_pau', 'te_escribio', 'fijados', 'con_santi', 'problema', 'en_marcha', 'sin_pendiente', 'se_van'] as const;
export type Grupo = (typeof GRUPOS)[number];

/** Los grupos de «te toca» (1-3 y el problema para Pau): van arriba, ordenados por cuánto llevan esperando. */
const URGENTES: readonly Grupo[] = ['autorizacion', 'bloqueado', 'problema_pau', 'te_escribio'];

/** Los tres niveles de «te toca»: lo que cuenta la primera línea del resumen. */
export const TE_TOCA: readonly Situacion[] = ['autorizacion', 'bloqueado', 'te_escribio'];

/**
 * 29-10 — ¿se está yendo? (su cierre de verdad, no «integrar y seguir»). Una sola fuente para la lista y la escena: va
 * al grupo «se_van», sin avisos, y en la escena a la fila de la salida.
 */
export function seVa(c: Pick<ContratadoDTO, 'situacion'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>): boolean {
  return c.situacion === 'saliendo' || (c.situacion === 'cerrando' && c.modoCierre === 'cerrar');
}

/**
 * Lo que la lista sabe de los contratos con Santi de UN agente (pelotas/porAgente.ts lo calcula de la lista de contratos):
 * `conSanti` = tiene contratos abiertos y en TODOS la pelota la tiene Santi; `esperaTuSi` = hay una tarjeta para Santi
 * esperando el clic de Pau.
 */
export interface SantiDelAgente {
  conSanti: boolean;
  esperaTuSi: boolean;
}
export type SantiPorAgente = ReadonlyMap<string, SantiDelAgente>;

/** El grupo de un contratado (como grupoDe, pero separa el problema que necesita a Pau y lo que está con Santi). */
export function grupoDelContratado(c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'fijado' | 'modoCierre'>>, santi?: SantiDelAgente): Grupo {
  if (seVa(c)) return 'se_van';
  // Una tarjeta para Santi esperando tu clic te toca: como un mensaje que espera respuesta (si no te toca ya algo más).
  if (santi?.esperaTuSi && !leToca(c)) return 'te_escribio';
  if (c.fijado && !leToca(c)) return 'fijados';
  if (santi?.conSanti && !leToca(c)) return 'con_santi';
  return problemaNecesitaPau(c) ? 'problema_pau' : grupoDe(c.situacion);
}

export function grupoDe(s: Situacion): Exclude<Grupo, 'problema_pau' | 'fijados' | 'se_van'> {
  if (s === 'trabajando' || s === 'cerrando' || s === 'llegando') return 'en_marcha';
  if (s === 'saliendo') return 'sin_pendiente';
  return s;
}

/**
 * Identidad visual de cada situación. Las clases van escritas enteras (Tailwind solo genera lo que lee).
 * Contraste sobre el panel (#121214): todos los colores de texto ≥ 4,5:1 (AA); el icono va sobre su color
 * sólido con tinta oscura, así que nunca depende solo del color: cada situación tiene su icono.
 */
export interface Aspecto {
  /** null = sin insignia (7 · sin nada pendiente: apagado, sin icono que confundir con otro gris). */
  icono: LucideIcon | null;
  /** Texto e icono sueltos en su color. */
  texto: string;
  /** Texto de la frase dentro de una fila teñida (un tono más claro). */
  textoFila: string;
  /** Insignia: fondo sólido + tinta oscura. */
  insignia: string;
  /** Fila teñida (solo 1 y 2: lo que de verdad te para). */
  fila: string | null;
  /** Borde izquierdo de fila y tarjeta. */
  borde: string;
  /** Punto de la etiqueta en la escena cuando no va rellena. */
  punto: string;
}

const ASPECTO: Record<Situacion, Aspecto> = {
  // 1 · el más fuerte: el oro de Venuz (el único oro brillante de la lista: «actúa»), fila teñida + borde +
  // insignia sólida. Llave = permiso.
  autorizacion: {
    icono: KeyRound,
    texto: 'text-oficina-acento',
    textoFila: 'text-oficina-te-necesita-claro',
    insignia: 'bg-oficina-acento text-oficina-acento-tinta',
    fila: 'bg-oficina-acento/[0.13] hover:bg-oficina-acento/[0.19]',
    borde: 'border-oficina-acento',
    punto: 'bg-oficina-acento',
  },
  // 2 · fuerte: violeta, fila teñida + borde. Pausa = «parado hasta que le contestes» (se lee bien a 11 px;
  // la mano quedaba en un borrón). No choca con «Interrumpir»: ese botón solo sale si está trabajando.
  bloqueado: {
    icono: Pause,
    texto: 'text-oficina-situacion-bloqueado',
    textoFila: 'text-oficina-situacion-bloqueado-claro',
    insignia: 'bg-oficina-situacion-bloqueado text-oficina-situacion-bloqueado-tinta',
    fila: 'bg-oficina-situacion-bloqueado/[0.09] hover:bg-oficina-situacion-bloqueado/[0.15]',
    borde: 'border-oficina-situacion-bloqueado',
    punto: 'bg-oficina-situacion-bloqueado',
  },
  // 3 · como un chat sin leer: neutro (blanco), sin teñir; negrita + punto solo si no lo has leído. No va en
  // oro para que el oro siga significando «actúa» (permiso y botones).
  te_escribio: {
    icono: MessageCircle,
    texto: 'text-oficina-texto',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-texto text-oficina-fondo',
    fila: null,
    borde: 'border-oficina-texto2',
    punto: 'bg-oficina-texto',
  },
  // 4 · problema técnico que la oficina está arreglando sola: tono tranquilo (arena), nunca rojo.
  problema: {
    icono: TriangleAlert,
    texto: 'text-oficina-situacion-problema',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-situacion-problema text-oficina-situacion-problema-tinta',
    fila: null,
    borde: 'border-oficina-situacion-problema',
    punto: 'bg-oficina-situacion-problema',
  },
  // 5 · trabajando (y empezando): verde de «en línea», un solo icono.
  trabajando: {
    icono: Activity,
    texto: 'text-oficina-trabajando',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-trabajando text-oficina-trabajando-tinta',
    fila: null,
    borde: 'border-oficina-trabajando',
    punto: 'bg-oficina-trabajando',
  },
  // 6 · cerrando: la oficina comprueba su trabajo. Gris neutro con portapapeles (el único gris con insignia).
  cerrando: {
    icono: ClipboardCheck,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-texto2 text-oficina-panel',
    fila: null,
    borde: 'border-oficina-texto2',
    punto: 'bg-oficina-texto2',
  },
  llegando: {
    icono: Activity,
    texto: 'text-oficina-trabajando',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-trabajando text-oficina-trabajando-tinta',
    fila: null,
    borde: 'border-oficina-trabajando',
    punto: 'bg-oficina-trabajando',
  },
  // 7 · apagado y SIN insignia.
  sin_pendiente: {
    icono: null,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: '',
    fila: null,
    borde: 'border-transparent',
    punto: 'bg-oficina-situacion-apagado',
  },
  saliendo: {
    icono: null,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: '',
    fila: null,
    borde: 'border-transparent',
    punto: 'bg-oficina-situacion-apagado',
  },
};

// Con Santi: el cian de sistema de Apple con el apretón de manos (el mismo en la lista, la oficina y su tablero).
const CON_SANTI: Aspecto = {
  icono: Handshake,
  texto: 'text-oficina-santi',
  textoFila: 'text-oficina-texto2',
  insignia: 'bg-oficina-santi text-oficina-santi-tinta',
  fila: null,
  borde: 'border-oficina-santi',
  punto: 'bg-oficina-santi',
};

// Fijados: el grupo lleva la chincheta; cada fila conserva la insignia de su situación.
const FIJADOS: Aspecto = {
  icono: Pin,
  texto: 'text-oficina-texto',
  textoFila: 'text-oficina-texto2',
  insignia: '',
  fila: null,
  borde: 'border-transparent',
  punto: 'bg-oficina-situacion-apagado',
};

// 4 · problema en el que Pau SÍ tiene que actuar (el agente le pide algo): rojo.
const PROBLEMA_PAU: Aspecto = {
  icono: TriangleAlert,
  texto: 'text-oficina-situacion-problema-pau',
  textoFila: 'text-oficina-situacion-problema-pau',
  insignia: 'bg-oficina-situacion-problema-pau text-oficina-situacion-problema-pau-tinta',
  fila: null,
  borde: 'border-oficina-situacion-problema-pau',
  punto: 'bg-oficina-situacion-problema-pau',
};

/**
 * ¿Un problema técnico necesita a Pau? Solo si el agente le pide algo (`pide`); si no, lo está resolviendo la
 * oficina sola y se pinta tranquilo. (El servidor no da otra señal; ver informe S12.)
 */
export function problemaNecesitaPau(c: Pick<ContratadoDTO, 'situacion' | 'pide'>): boolean {
  return c.situacion === 'problema' && c.pide.trim() !== '';
}

/** El aspecto de UN contratado (igual que el de su situación salvo el problema que necesita a Pau). */
export function aspectoDe(c: Pick<ContratadoDTO, 'situacion' | 'pide'>): Aspecto {
  return problemaNecesitaPau(c) ? PROBLEMA_PAU : ASPECTO[c.situacion];
}

export function aspecto(s: Situacion): Aspecto {
  return ASPECTO[s];
}

/** El aspecto de un grupo es el de su primera situación (En marcha = trabajando; Sin nada pendiente = sin_pendiente). */
export function aspectoGrupo(g: Grupo): Aspecto {
  if (g === 'fijados') return FIJADOS;
  if (g === 'con_santi') return CON_SANTI;
  if (g === 'problema_pau') return PROBLEMA_PAU;
  if (g === 'se_van') return ASPECTO.saliendo;
  return ASPECTO[g === 'en_marcha' ? 'trabajando' : g];
}

/** Desde cuándo te espera (1-3) — la urgencia se ordena por tiempo: el que más lleva, primero. */
export function esperaDesde(c: Pick<ContratadoDTO, 'teNecesitaDesde' | 'ultimoMensaje' | 'ultimoInformeEn' | 'creadoEn'>): string {
  return c.teNecesitaDesde ?? c.ultimoMensaje?.en ?? ultimoMovimiento(c);
}

export function teToca(s: Situacion): boolean {
  return TE_TOCA.includes(s);
}

/** ¿Le toca a Pau? (1-3, más el problema en el que tiene que actuar.) */
export function leToca(c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>): boolean {
  if (seVa(c)) return false; // 29-10: lo que se va no avisa
  return teToca(c.situacion) || problemaNecesitaPau(c);
}

/** Un grupo de la lista. Solo «fijados» lleva `subgrupos`: sus miembros (los tranquilos) repartidos por estado, en el orden de la lista. */
export interface GrupoConMiembros<C> {
  grupo: Grupo;
  miembros: C[];
  subgrupos?: { grupo: Exclude<Grupo, 'fijados'>; miembros: C[] }[];
}

/**
 * Los grupos con gente, en su orden. Dentro de 1-3 el que más lleva esperando primero; en el resto, lo más reciente.
 * 06-10 (Pau): los fijados se agrupan por dentro por estado igual que el resto («lo que necesite mi atención lo más
 * arriba posible, lo que trabaja de fondo más abajo»); `miembros` del grupo fijados es la concatenación de sus
 * subgrupos, así el orden plano (teclado, móvil) es el que se ve. 09-10: el fijado al que le toca algo a Pau no se
 * queda en «Fijados» (taparía un permiso de otro): va a su grupo de urgencia; «Fijados» va detrás de lo urgente.
 */
export function agruparPorSituacion<C extends ContratadoDTO>(lista: readonly C[], noLeido: (c: C) => boolean = () => false, santi?: SantiPorAgente): GrupoConMiembros<C>[] {
  const tranquilo = (c: C) => c.fijado && !leToca(c) && !seVa(c) && !santi?.get(c.agentId)?.esperaTuSi;
  const fijados = porEstado(lista.filter(tranquilo), noLeido);
  const resto = porEstado(lista.filter((c) => !tranquilo(c)), noLeido, santi);
  const grupo: GrupoConMiembros<C>[] = fijados.length ? [{ grupo: 'fijados', miembros: fijados.flatMap((g) => g.miembros), subgrupos: fijados }] : [];
  const urgente = resto.filter((g) => URGENTES.includes(g.grupo));
  return [...urgente, ...grupo, ...resto.filter((g) => !URGENTES.includes(g.grupo))];
}

/** Reparte por grupo de estado (sin mirar el fijado) y ordena dentro de cada uno. */
function porEstado<C extends ContratadoDTO>(lista: readonly C[], noLeido: (c: C) => boolean, santi?: SantiPorAgente): { grupo: Exclude<Grupo, 'fijados'>; miembros: C[] }[] {
  return GRUPOS.filter((g): g is Exclude<Grupo, 'fijados'> => g !== 'fijados')
    .map((grupo) => {
      const miembros = lista.filter((c) => grupoDelContratado({ situacion: c.situacion, pide: c.pide, modoCierre: c.modoCierre }, santi?.get(c.agentId)) === grupo);
      const urgente = URGENTES.includes(grupo);
      miembros.sort(
        (a, b) =>
          // 3 · te ha escrito: lo no leído primero (como un chat), luego el que más lleva esperando.
          (grupo === 'te_escribio' ? Number(noLeido(b)) - Number(noLeido(a)) : 0) ||
          (urgente ? esperaDesde(a).localeCompare(esperaDesde(b)) : ultimoMovimiento(b).localeCompare(ultimoMovimiento(a))),
      );
      return { grupo, miembros };
    })
    .filter((g) => g.miembros.length > 0);
}

/** Orden plano (para Alt+↑/↓ y el móvil): el mismo que la lista agrupada. */
export function ordenarPorSituacion<C extends ContratadoDTO>(lista: readonly C[], noLeido?: (c: C) => boolean, santi?: SantiPorAgente): C[] {
  return agruparPorSituacion(lista, noLeido, santi).flatMap((g) => g.miembros);
}

const PASO_CIERRE: Record<NonNullable<ContratadoDTO['pasoCierre']>, string> = {
  esperando_informe: 'oficinaV2.s12.cierre.esperando_informe',
  comprobando: 'oficinaV2.s12.cierre.comprobando',
  revisando: 'oficinaV2.s12.cierre.revisando',
  esperando_permiso: 'oficinaV2.s12.cierre.esperando_permiso',
  publicando: 'oficinaV2.s12.cierre.publicando',
  en_cola: 'oficinaV2.s12.cierre.en_cola',
  archivando: 'oficinaV2.s12.cierre.archivando',
};

/**
 * WS15-D8 — el paso del cierre en una frase. Integrando (sigue después), lo que escribe es su «nota de entrega»; al irse,
 * su «informe de cierre» (Pau, 17-10: que se llamaran igual «me confunde»). Lo usan la fila y el expediente.
 */
export function pasoCierreEnFrase(paso: NonNullable<ContratadoDTO['pasoCierre']>, modo: 'cerrar' | 'integrar' | null | undefined): string {
  if (modo === 'integrar' && paso === 'esperando_informe') return t('oficinaV2.s12.cierre.esperando_nota');
  return t(PASO_CIERRE[paso] as 'oficinaV2.s12.cierre.comprobando');
}

/**
 * 09-10 (Pau aprobó «Terminado» frente a «te pregunta»): en «Esperan tu respuesta», ¿es porque ha acabado su trabajo
 * (te lo entrega) y no porque te pregunte algo? El agente lo dice en su parte (fase «cerrando»: está acabando);
 * «terminado» lo pone la oficina al irse. Solo cuenta si NO te ha dejado una pregunta (`pide`).
 */
export function haTerminado(c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'fase'>>): boolean {
  return c.situacion === 'te_escribio' && (c.fase === 'terminado' || c.fase === 'cerrando') && c.pide.trim() === '';
}

/**
 * 09-10 — «sin noticias · 25 min»: trabaja, pero lleva rato sin contar nada (el servidor decide `sinNoticias`, 15 min).
 * La fila lo dice en vez de la hora; null si tiene noticias.
 */
export function sinNoticiasDesde(c: Pick<ContratadoDTO, 'sinNoticias' | 'ultimoMensaje' | 'ultimoInformeEn' | 'creadoEn'>, ahora = Date.now()): string | null {
  if (!c.sinNoticias) return null;
  const min = Math.max(0, Math.round((ahora - Date.parse(ultimoMovimiento(c))) / 60_000));
  if (!Number.isFinite(min)) return null;
  const h = Math.round(min / 60);
  const cuanto =
    min < 60 ? t('oficinaV2.s12.duracion.min', { n: min }) : h < 24 ? t('oficinaV2.s12.duracion.h', { n: h }) : t('oficinaV2.s12.duracion.d', { n: Math.round(h / 24) });
  return t('oficinaV2.s12.sinNoticias', { cuanto });
}

/**
 * La UNA frase útil de la fila (brief: sin repetir el nombre del grupo). Para 1 la acción que pide («Pide
 * permiso: …»); para 2 lo que necesita en sus palabras; para 3 su último mensaje o su pregunta («Terminado: …» si
 * ha acabado su trabajo).
 */
export function fraseDeFila(c: Pick<ContratadoDTO, 'situacion' | 'pide' | 'titular' | 'ultimoMensaje' | 'pasoCierre'> & Partial<Pick<ContratadoDTO, 'modoCierre' | 'fase'>>): string {
  const pide = c.pide.trim();
  const titular = c.titular.trim();
  switch (c.situacion) {
    case 'autorizacion':
      // Sin «Pide permiso:» (el grupo ya lo dice); en la franja del chat sí va.
      return pide ? mayuscula(pide) : t('oficinaV2.s12.fila.permisoSinTexto');
    case 'bloqueado':
      return pide || t('oficinaV2.s12.fila.bloqueadoSinTexto');
    case 'te_escribio': {
      if (haTerminado(c)) {
        // Sin «Terminado: Terminado: …» si su mensaje ya empieza así.
        const que = (c.ultimoMensaje?.texto.trim() || titular).replace(/^(terminado|finished|done)\b\s*[:.,-]?\s*/i, '');
        return t('oficinaV2.s12.fila.terminado', { que: que ? mayuscula(que) : t('oficinaV2.s12.fila.terminadoSinTexto') });
      }
      return pide || c.ultimoMensaje?.texto.trim() || titular || t('oficinaV2.s12.fila.teEscribioSinTexto');
    }
    case 'problema':
      if (pide) return pide;
      return t('oficinaV2.s12.fila.problemaOficina', { que: titular || t('oficinaV2.s12.fila.problemaSinTexto') });
    case 'trabajando':
      return titular || t('oficinaV2.s12.fila.trabajandoSinTexto');
    case 'cerrando': {
      const paso = c.pasoCierre ? pasoCierreEnFrase(c.pasoCierre, c.modoCierre) : t('oficinaV2.s12.fila.cerrandoSinTexto');
      // 30-09 — integrar sin irse: que no parezca que se va.
      return c.modoCierre === 'integrar' ? t('oficinaV2.s12.fila.integrando', { paso: paso.charAt(0).toLowerCase() + paso.slice(1) }) : paso;
    }
    case 'llegando':
      return t('oficinaV2.s12.fila.llegando');
    case 'saliendo':
      return t('oficinaV2.s12.fila.saliendo');
    default:
      return titular || t('oficinaV2.s12.fila.sinPendienteSinTexto');
  }
}

function mayuscula(x: string): string {
  return x.charAt(0).toUpperCase() + x.slice(1);
}

/** Nombre corto de la situación (cabecera del chat y lectores de pantalla). */
export function etiquetaSituacion(s: Situacion): string {
  return t(`oficinaV2.s12.situacion.${s}`);
}

export function tituloGrupo(g: Grupo): string {
  return t(`oficinaV2.s12.grupo.${g}`);
}

export interface Pieza {
  grupo: Grupo;
  n: number;
  texto: string;
}

/**
 * El resumen de la cabecera del panel. `teToca`: «2 permisos · 2 no pueden seguir · 1 problema para ti · 3 te
 * han escrito» (solo lo que hay). `aparte`, con las palabras de los grupos: «4 trabajando · 1 cerrando · 1 con
 * un problema» (este último: solo los que arregla la oficina).
 */
export function resumenCabecera(lista: readonly (Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>)[]): { teToca: Pieza[]; aparte: Pieza[] } {
  const cuenta = (f: (c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>) => boolean) => lista.filter(f).length;
  const es = (...s: Situacion[]) => (c: Pick<ContratadoDTO, 'situacion'>) => s.includes(c.situacion);
  const pieza = (grupo: Grupo, n: number, uno: string, varios: string): Pieza[] =>
    n ? [{ grupo, n, texto: t((n === 1 ? uno : varios) as 'oficinaV2.s12.resumen.permisoUno', { n }) }] : [];
  return {
    teToca: [
      ...pieza('autorizacion', cuenta(es('autorizacion')), 'oficinaV2.s12.resumen.permisoUno', 'oficinaV2.s12.resumen.permisoVarios'),
      ...pieza('bloqueado', cuenta(es('bloqueado')), 'oficinaV2.s12.resumen.bloqueadoUno', 'oficinaV2.s12.resumen.bloqueadoVarios'),
      ...pieza('problema_pau', cuenta(problemaNecesitaPau), 'oficinaV2.s12.resumen.problemaPauUno', 'oficinaV2.s12.resumen.problemaPauVarios'),
      ...pieza('te_escribio', cuenta(es('te_escribio')), 'oficinaV2.s12.resumen.teEscribioUno', 'oficinaV2.s12.resumen.teEscribioVarios'),
    ],
    aparte: [
      ...pieza('en_marcha', cuenta(es('trabajando', 'llegando')), 'oficinaV2.s12.resumen.trabajando', 'oficinaV2.s12.resumen.trabajando'),
      // 29-10: los que se van no cuentan (están al fondo, sin ruido); solo los que cierran para integrar y seguir.
      ...pieza('en_marcha', cuenta((c) => es('cerrando')(c) && !seVa(c)), 'oficinaV2.s12.resumen.cerrando', 'oficinaV2.s12.resumen.cerrando'),
      ...pieza('problema', cuenta((c) => c.situacion === 'problema' && !problemaNecesitaPau(c)), 'oficinaV2.s12.resumen.problemaUno', 'oficinaV2.s12.resumen.problemaVarios'),
    ],
  };
}

/** Marcador de la escena (el «mundo») con la identidad S12: lo usa Mundo SOLO si se le pasa (maqueta). */
export interface MarcadorS12 {
  /** Insignia sobre la cabeza + etiqueta rellena (1-3 y el problema de Pau); null = etiqueta oscura con punto. */
  icono: LucideIcon | null;
  relleno: string | null;
  punto: string | null;
  late: boolean;
  texto: string;
}

export function marcadorEscena(c: ContratadoDTO): MarcadorS12 {
  const a = aspectoDe(c);
  // 09-10 — el problema que arregla la oficina sola no destaca (solo su punto arena): la insignia, para lo de Pau.
  const destaca = leToca(c);
  return {
    icono: destaca ? a.icono : null,
    relleno: destaca ? a.insignia : null,
    punto: c.situacion === 'sin_pendiente' || c.situacion === 'saliendo' ? null : a.punto,
    // Solo late lo que de verdad le para (1 y 2); «te ha escrito» no alarma.
    late: c.situacion === 'autorizacion' || c.situacion === 'bloqueado' || problemaNecesitaPau(c),
    texto: fraseDeFila(c),
  };
}
